import { type PlaybackState, ZoomLevel } from "Interfaces";
import { createAppSlice } from "Data/Redux/Store";
import type { PayloadAction } from "@reduxjs/toolkit";
import { ceilingDate, type DateString, DateUnits, floorDate } from "@clintonelec/typescriptutils";

const initialState: PlaybackState = {
	clipDragging: false,
	clipEnd:  new Date().toISOString(),
	clipModeActive: false,
	clipName: "",
	clipStart:  new Date().toISOString(),
	date: null,
	videoPlayerTime: null,
	scrollTimelineStart: new Date().toISOString(),
	scrollTimelineEnd: new Date().toISOString(),
	zoomLevel: ZoomLevel.SIXTY_MINUTES
};

export const setClipDraggingReducer = (state: PlaybackState, action: PayloadAction<boolean>) => {
	state.clipDragging = action?.payload;
};

export const setClipModeActiveReducer = (state: PlaybackState, action: PayloadAction<boolean>) => {
	const { clipModeActive, videoPlayerTime } = state;

	if (!videoPlayerTime) {
		return;
	}

	const videoPlayerDate = new Date(videoPlayerTime);
	let startTime = new Date(videoPlayerDate.getTime() - (240 * DateUnits.SECONDS));
	let endTime = new Date(videoPlayerDate.getTime() + (240 * DateUnits.SECONDS));

	startTime = new Date(Math.min(startTime.getTime(), new Date().getTime() - (60 * DateUnits.SECONDS)));

	startTime = new Date(Math.max(startTime.getTime(), floorDate(videoPlayerDate).getTime()));
	endTime = new Date(Math.min(endTime.getTime(), new Date().getTime(), ceilingDate(videoPlayerDate).getTime()));

	if (action.payload && !clipModeActive) {
		state.clipStart = startTime.toISOString();
		state.clipEnd = endTime.toISOString();
		state.zoomLevel = ZoomLevel.ONE_MINUTE;
		state.date = state.videoPlayerTime;
	}

	state.clipModeActive = action.payload;
	state.clipDragging = false;
	state.clipName = "";
};

export const moveClipReducer = (state: PlaybackState, action: PayloadAction<DateString>) => {
	if (!state.date) {
		return;
	}

	const halfClipLength = Math.abs(new Date(state.clipStart).getTime() - new Date(state.clipEnd).getTime()) / 2;
	const payloadTime = new Date(action.payload).getTime();
	const newStart = new Date(payloadTime - halfClipLength);
	const newEnd = new Date(payloadTime + halfClipLength);
	const withinBounds = newStart >= floorDate(new Date(state.date)) && newEnd <= ceilingDate(new Date(state.date));

	if (newEnd <= new Date() && withinBounds) {
		state.clipStart = newStart.toISOString();
		state.clipEnd = newEnd.toISOString();
	}
};

export const updateClipStartReducer = (state: PlaybackState, action: PayloadAction<DateString>) => {
	const { clipEnd } = state;
	const clipStart = action.payload;
	const maxStart = new Date(clipEnd).getTime() - DateUnits.MINUTES;

	if (new Date(clipStart).getTime() <= maxStart) {
		state.clipStart = clipStart;
	} else {
		state.clipStart = new Date(maxStart).toISOString();
	}
};

export const updateClipEndReducer = (state: PlaybackState, action: PayloadAction<DateString>) => {
	const { clipStart } = state;
	const clipEnd = action.payload;
	const now = new Date().toISOString();
	const minEnd = new Date(clipStart).getTime() + DateUnits.MINUTES;

	if (clipEnd <= now && new Date(clipEnd).getTime() >= minEnd) {
		state.clipEnd = clipEnd;
	} else if (clipEnd >= now) {
		state.clipEnd = now;
	} else {
		state.clipEnd = new Date(minEnd).toISOString();
	}
};

export const setVideoPlayerTimeReducer = (state: PlaybackState, action: PayloadAction<DateString>) => {
	state.videoPlayerTime = action.payload;
};

export const setScrollTimelineStartReducer = (state: PlaybackState, action: PayloadAction<DateString>) => {
	state.scrollTimelineStart = action.payload;
};

export const setScrollTimelineEndReducer = (state: PlaybackState, action: PayloadAction<DateString>) => {
	state.scrollTimelineEnd = action.payload;
};

export const setDateReducer = (state: PlaybackState, action: PayloadAction<DateString | null>) => {
	state.date = action.payload;

	if (action.payload) {
		state.videoPlayerTime = new Date(action.payload).toISOString();
	} else {
		state.videoPlayerTime = null;
	}
};

const setZoomLevelReducer = (state: PlaybackState, action: PayloadAction<ZoomLevel>) => {
	state.zoomLevel = action.payload;
	state.date = state.videoPlayerTime;
};

const setClipNameReducer = (state: PlaybackState, action: PayloadAction<string>) => {
	state.clipName = action.payload;
};

const getClipDragging = (state: PlaybackState) => {
	return state.clipDragging;
};

const getClipEnd = (state: PlaybackState) => {
	return state.clipEnd;
};

const getVideoPlayerTime = (state: PlaybackState) => {
	return state.videoPlayerTime;
};

const getClipStart = (state: PlaybackState) => {
	return state.clipStart;
};

const getClipModeActive = (state: PlaybackState) => {
	return state.clipModeActive;
};

const getClipName = (state: PlaybackState) => {
	return state.clipName;
};

const getScrollTimelineStart = (state: PlaybackState) => {
	return state.scrollTimelineStart;
};

const getScrollTimelineEnd = (state: PlaybackState) => {
	return state.scrollTimelineEnd;
};

export const getDate = (state: PlaybackState) => {
	return state.date ? new Date(state.date) : null;
};

const getZoomLevel = (state: PlaybackState) => {
	return state.zoomLevel;
};

const playbackSlice = createAppSlice({
	name: "playback",
	initialState,
	reducers: (create) => ({
		moveClipAction: create.reducer(moveClipReducer),
		updateClipStartAction: create.reducer(updateClipStartReducer),
		updateClipEndAction: create.reducer(updateClipEndReducer),
		setClipDraggingAction: create.reducer(setClipDraggingReducer),
		setClipModeActiveAction: create.reducer(setClipModeActiveReducer),
		setClipNameAction: create.reducer(setClipNameReducer),
		setVideoPlayerTimeAction: create.reducer(setVideoPlayerTimeReducer),
		setDateAction: create.reducer(setDateReducer),
		setScrollTimelineEndAction: create.reducer(setScrollTimelineEndReducer),
		setScrollTimelineStartAction: create.reducer(setScrollTimelineStartReducer),
		setZoomLevelAction: create.reducer(setZoomLevelReducer)
	}),
	selectors: {
		selectClipDragging: getClipDragging,
		selectClipEnd: getClipEnd,
		selectClipStart: getClipStart,
		selectClipModeActive: getClipModeActive,
		selectClipName: getClipName,
		selectVideoPlayerTime: getVideoPlayerTime,
		selectDate: getDate,
		selectScrollTimelineStart: getScrollTimelineStart,
		selectScrollTimelineEnd: getScrollTimelineEnd,
		selectZoomLevel: getZoomLevel
	}
});

export default playbackSlice;

export const {
	moveClipAction, updateClipEndAction, updateClipStartAction, setClipDraggingAction, setClipModeActiveAction,
	setDateAction, setScrollTimelineEndAction, setScrollTimelineStartAction, setZoomLevelAction, setClipNameAction,
	setVideoPlayerTimeAction
} = playbackSlice.actions;

export const {
	selectClipDragging, selectClipEnd, selectClipStart, selectClipModeActive, selectDate, selectScrollTimelineEnd,
	selectScrollTimelineStart, selectZoomLevel, selectClipName, selectVideoPlayerTime
} = playbackSlice.selectors;
